import { useContext, useEffect, useState } from 'react';
import { ISliderContext } from 'Feature/Slider/Models/Panels';
import { SliderContext } from 'Feature/Slider/SliderContext';
import { findFavorites, getPanelLinks } from './FavoritesHelper';
import SitecoreLink from 'Foundation/GlassMapper/SitecoreLink';
import './ManageMyFavorites.scss';
import { IconDrag } from '@lmig/lmds-react-icons';
import { Tooltip } from 'Foundation/Common/Tooltip';

const ManageMyFavorites = () => {
  const sliderContext = useContext<ISliderContext>(SliderContext);
  const [favorites, setFavorites] = useState<INavigationLink[]>([]);
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [dragIndex, setDragIndex] = useState<number>();
  const [dropIndex, setDropIndex] = useState<number>();
  const maxFavorites = 8;

  useEffect(() => {
    document.title = 'Manage my favorites';
    if (sliderContext.panels) {
      let linkSections: IReactNavSection[] = getPanelLinks(sliderContext.panels);
      if (linkSections.length > 0 && sliderContext.favorites) {
        let links: INavigationLink[] = findFavorites(sliderContext.favorites, linkSections);
        if (links) setFavorites(links);
      }
    }
  }, [sliderContext.panels, sliderContext.favorites]);

  const allowDrop = (ev: React.DragEvent<HTMLDivElement>, index: number) => {
    ev.preventDefault();
    if (dropIndex !== index) setDropIndex(index);
  };

  const drag = (ev: React.DragEvent<HTMLDivElement>, index: number) => {
    setIsDragging(true);
    if (dragIndex !== index) setDragIndex(index);
  };

  const drop = (ev: React.DragEvent<HTMLDivElement>) => {
    setIsDragging(false);
    ev.preventDefault();
    if (dragIndex !== undefined && dropIndex !== undefined && dragIndex !== dropIndex) {
      let newFavorites: INavigationLink[];
      let reordered = favorites;
      if (dropIndex > dragIndex) {
        reordered.splice(dropIndex + 1, 0, reordered[dragIndex]);
        newFavorites = reordered.filter((x, i) => i !== dragIndex);
        setFavorites(newFavorites);
      } else {
        reordered.splice(dropIndex, 0, reordered[dragIndex]);
        newFavorites = reordered.filter((x, i) => i !== dragIndex + 1);
        setFavorites(newFavorites);
      }
      updateFavorites(newFavorites);
      sliderContext.setFavorites(newFavorites.map((x) => x.Link.TargetId as string));
    }
  };

  const updateFavorites = async (newFavorites: INavigationLink[]) => {
    let reordered = newFavorites.map((favorite) => favorite.Link.TargetId ?? '');
    if (reordered) {
      await fetch('/api/sitecore/NowNavigationAsync/UpdateFavorites', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ userFavorites: reordered }),
      });
    }
  };

  return (
    <div className="manage-my-favorites">
      <p className="instructional-text">
        Use the icons on the left to drag a link and place it in the order that you prefer. The updated order will be
        displayed in your dashboard.
      </p>
      <div className="manage-my-favorites-links">
        {favorites && favorites.length > 0 ? (
          <>
            {favorites.slice(0, maxFavorites).map((link: INavigationLink, index: number) => (
              <div
                className={`menu-section-link${isDragging && index === dropIndex ? ' dropable' : ''}`}
                key={index}
                draggable
                onDragStart={(event) => drag(event, index)}
                data-index={index}
                onDragOver={(event) => allowDrop(event, index)}
                onDrop={(event) => drop(event)}
                onDragEnd={() => setIsDragging(false)}
              >
                <Tooltip text="Drag to reorder">
                  <IconDrag color="teal" size="24" title="" aria-label="Drag to reorder" />
                </Tooltip>{' '}
                <SitecoreLink
                  Class={link.Link.Class}
                  Goal={link.Link.Goal}
                  key={link.Link.Text}
                  Target={link.Link.Target}
                  Text={link.Link.Text}
                  Url={link.Link.Url}
                  TargetId={link.Link.TargetId}
                  Favoritable={true}
                  CaretPosition={'none'}
                  SubText={link.Link.SubText}
                />
              </div>
            ))}
            {favorites.length > maxFavorites && (
              <div className="water-line-message">Links below are not visible in your dashboard</div>
            )}
            <div className="other-favorites">
              {favorites.slice(8).map((link: INavigationLink, index: number) => (
                <div
                  className={`menu-section-link${isDragging && index + maxFavorites === dropIndex ? ' dropable' : ''}`}
                  draggable
                  key={index + maxFavorites}
                  onDragStart={(event) => drag(event, index + maxFavorites)}
                  data-index={index + maxFavorites}
                  onDragOver={(event) => allowDrop(event, index + maxFavorites)}
                  onDrop={(event) => drop(event)}
                  onDragEnd={() => setIsDragging(false)}
                >
                  <Tooltip text="Drag to reorder">
                    <IconDrag color="teal" size="24" title="" aria-label="Drag to reorder" />
                  </Tooltip>{' '}
                  <SitecoreLink
                    Class={link.Link.Class}
                    Goal={link.Link.Goal}
                    key={link.Link.Text}
                    Target={link.Link.Target}
                    Text={link.Link.Text}
                    Url={link.Link.Url}
                    TargetId={link.Link.TargetId}
                    Favoritable={true}
                    CaretPosition={'none'}
                    SubText={link.Link.SubText}
                  />
                </div>
              ))}
            </div>
          </>
        ) : (
          <div className="navigation-section">
            <div className="no-favorites">Your favorites will appear here.</div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ManageMyFavorites;
